import React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import sanitizeHtml from 'sanitize-html';
import { cva, type VariantProps } from 'class-variance-authority';
import cn from '../utils/cn';

const tooltipContent = cva(
  'z-tooltip max-w-xs rounded-md px-3 py-1.5 text-xs font-medium text-center shadow-md',
  {
    variants: {
      variant: {
        dark: 'bg-k-h text-white',
        brand: 'bg-brand text-brand-foreground',
        danger: 'bg-red-500 text-white',
        warning: 'bg-orange-500 text-white',
        success: 'bg-green-600 text-white',
        info: 'bg-blue-500 text-white',
      },
    },
    defaultVariants: { variant: 'dark' },
  },
);

type Variant = NonNullable<VariantProps<typeof tooltipContent>['variant']>;

const arrowFill: Record<Variant, string> = {
  dark: 'fill-k-h',
  brand: 'fill-brand',
  danger: 'fill-red-500',
  warning: 'fill-orange-500',
  success: 'fill-green-600',
  info: 'fill-blue-500',
};

// margaret theme color keys → variants
const legacyColors: Record<string, Variant> = {
  purple: 'dark',
  orange: 'brand',
  red: 'danger',
  danger: 'danger',
  green: 'success',
  blue: 'info',
};

type Side = 'top' | 'right' | 'bottom' | 'left';
type Align = 'start' | 'center' | 'end';

/** Maps legacy positions ('left-top', 'bottom-end', …) to side + align. */
function fromLegacyPosition(position: string): { side: Side; align: Align } {
  const [side, modifier] = position.split('-');
  const align =
    modifier === 'top' || modifier === 'start'
      ? 'start'
      : modifier === 'bottom' || modifier === 'end'
        ? 'end'
        : 'center';
  return { side: side as Side, align };
}

type KoobTooltipProps = VariantProps<typeof tooltipContent> & {
  tip: React.ReactNode;
  children: React.ReactNode;
  side?: Side;
  align?: Align;
  hasArrow?: boolean;
  disabled?: boolean;
  /** Extra classes on the trigger wrapper, e.g. to let it stretch. */
  className?: string;
  maxWidth?: number;
  delayDuration?: number;
  /** @deprecated use side / align */
  position?: string;
  /** @deprecated use variant */
  color?: string;
  /** @deprecated tooltip trigger is inline-flex; use style at the call site */
  block?: boolean;
  /** @deprecated Radix wires aria automatically */
  label?: string;
  /** @deprecated wrap tip content instead */
  textAlign?: React.CSSProperties['textAlign'];
};

const KoobTooltip = React.forwardRef<HTMLSpanElement, KoobTooltipProps>(
  (
    {
      tip,
      children,
      side,
      align,
      variant,
      hasArrow = true,
      disabled = false,
      className,
      maxWidth,
      delayDuration = 200,
      position,
      color,
      block,
      label,
      textAlign,
      ...props
    },
    ref,
  ) => {
    const legacy = position ? fromLegacyPosition(position) : undefined;
    const resolvedVariant =
      variant ?? (color ? (legacyColors[color] ?? 'dark') : undefined);

    return (
      <TooltipPrimitive.Provider delayDuration={delayDuration}>
        <TooltipPrimitive.Root>
          <TooltipPrimitive.Trigger asChild>
            <span
              ref={ref}
              className={cn(
                '[&_svg]:block',
                block ? 'inline' : 'flex',
                className,
              )}
              {...props}
            >
              {children}
            </span>
          </TooltipPrimitive.Trigger>
          {!disabled && Boolean(tip) && (
            <TooltipPrimitive.Portal>
              <TooltipPrimitive.Content
                side={side ?? legacy?.side ?? 'bottom'}
                align={align ?? legacy?.align ?? 'center'}
                sideOffset={4}
                className={tooltipContent({ variant: resolvedVariant })}
                style={{ maxWidth, textAlign }}
              >
                {typeof tip === 'string' ? (
                  // legacy Tooltip rendered string tips as HTML (translations
                  // contain markup); sanitized here because tips also carry
                  // API data
                  <span
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(tip) }}
                  />
                ) : (
                  tip
                )}
                {hasArrow && (
                  // fill attr defuses the global `svg:not([fill])` normalize
                  // rule that otherwise paints the arrow currentColor (white)
                  <TooltipPrimitive.Arrow
                    fill="currentColor"
                    className={arrowFill[resolvedVariant ?? 'dark']}
                  />
                )}
              </TooltipPrimitive.Content>
            </TooltipPrimitive.Portal>
          )}
        </TooltipPrimitive.Root>
      </TooltipPrimitive.Provider>
    );
  },
);

KoobTooltip.displayName = 'KoobTooltip';

export default KoobTooltip;
